import type { ViewportTransform } from "../coordinates/sourceToViewport";
import type { Shape, StrokeWidth } from "./shapes";

/**
 * renderShapes - draws annotation shapes through the shared ViewportTransform (D-05, D-08).
 *
 * Canvas-only: the same drawShapes call renders the live overlay (AnnotationCanvas, 05-15/05-16)
 * and the snapshot PNG (05-16), so annotation content is never injected as HTML (T-05-77).
 */

const ARROW_HEAD_ANGLE_RAD = (28 * Math.PI) / 180;
const SELECTION_OUTLINE_PADDING_PX = 6;
const SELECTION_DASH: number[] = [6, 4];

export type Canvas2DLike = {
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  closePath(): void;
  stroke(): void;
  save(): void;
  restore(): void;
  setLineDash(segments: number[]): void;
  // Real CanvasRenderingContext2D types strokeStyle as string | CanvasGradient | CanvasPattern;
  // kept as the full union so a real 2D context satisfies this interface structurally.
  strokeStyle: string | CanvasGradient | CanvasPattern;
  lineWidth: number;
  lineCap: string;
  lineJoin: string;
};

/** Stroke width (2/4/8, thousandths of the content's longer side) resolved to CSS px. */
export function lineWidthPx(width: StrokeWidth, contentRect: { width: number; height: number }): number {
  return Math.max(1, (width * Math.max(contentRect.width, contentRect.height)) / 1000);
}

function drawPoint(ctx: Canvas2DLike, p: { x: number; y: number }, moveOrLine: "moveTo" | "lineTo"): void {
  ctx[moveOrLine](p.x, p.y);
}

function drawArrow(ctx: Canvas2DLike, from: { x: number; y: number }, to: { x: number; y: number }, headLength: number): void {
  ctx.beginPath();
  drawPoint(ctx, from, "moveTo");
  drawPoint(ctx, to, "lineTo");
  ctx.stroke();

  const angle = Math.atan2(to.y - from.y, to.x - from.x);
  const back1 = angle + Math.PI - ARROW_HEAD_ANGLE_RAD;
  const back2 = angle + Math.PI + ARROW_HEAD_ANGLE_RAD;
  const head1 = { x: to.x + headLength * Math.cos(back1), y: to.y + headLength * Math.sin(back1) };
  const head2 = { x: to.x + headLength * Math.cos(back2), y: to.y + headLength * Math.sin(back2) };

  ctx.beginPath();
  drawPoint(ctx, head1, "moveTo");
  drawPoint(ctx, to, "lineTo");
  ctx.stroke();

  ctx.beginPath();
  drawPoint(ctx, head2, "moveTo");
  drawPoint(ctx, to, "lineTo");
  ctx.stroke();
}

function drawRect(ctx: Canvas2DLike, sourcePoints: readonly [number, number][], transform: ViewportTransform): void {
  const [p0, p1] = sourcePoints;
  const minX = Math.min(p0[0], p1[0]);
  const maxX = Math.max(p0[0], p1[0]);
  const minY = Math.min(p0[1], p1[1]);
  const maxY = Math.max(p0[1], p1[1]);
  // Each corner goes through the transform individually (not a viewport-space bounding box), so
  // a rotated frame still draws a correctly-rotated quadrilateral.
  const corners = [
    transform.toViewport({ x: minX, y: minY }),
    transform.toViewport({ x: maxX, y: minY }),
    transform.toViewport({ x: maxX, y: maxY }),
    transform.toViewport({ x: minX, y: maxY }),
  ];
  ctx.beginPath();
  drawPoint(ctx, corners[0], "moveTo");
  for (let i = 1; i < corners.length; i++) {
    drawPoint(ctx, corners[i], "lineTo");
  }
  ctx.closePath();
  ctx.stroke();
}

function drawPolyline(ctx: Canvas2DLike, points: { x: number; y: number }[]): void {
  if (points.length === 0) {
    return;
  }
  ctx.beginPath();
  drawPoint(ctx, points[0], "moveTo");
  for (let i = 1; i < points.length; i++) {
    drawPoint(ctx, points[i], "lineTo");
  }
  ctx.stroke();
}

function drawSelectionOutline(ctx: Canvas2DLike, points: { x: number; y: number }[]): void {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs) - SELECTION_OUTLINE_PADDING_PX;
  const maxX = Math.max(...xs) + SELECTION_OUTLINE_PADDING_PX;
  const minY = Math.min(...ys) - SELECTION_OUTLINE_PADDING_PX;
  const maxY = Math.max(...ys) + SELECTION_OUTLINE_PADDING_PX;

  ctx.setLineDash(SELECTION_DASH);
  ctx.beginPath();
  ctx.moveTo(minX, minY);
  ctx.lineTo(maxX, minY);
  ctx.lineTo(maxX, maxY);
  ctx.lineTo(minX, maxY);
  ctx.closePath();
  ctx.stroke();
  ctx.setLineDash([]);
}

/** Draws one shape through transform.toViewport - never from stored/mirrored pixel coordinates. */
export function drawShape(ctx: Canvas2DLike, shape: Shape, transform: ViewportTransform, selected = false): void {
  const viewportPoints = shape.points.map((p) => transform.toViewport({ x: p[0], y: p[1] }));
  const lw = lineWidthPx(shape.width, transform.contentRect);

  ctx.save();
  ctx.strokeStyle = shape.color;
  ctx.lineWidth = lw;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  if (shape.tool === "arrow" && viewportPoints.length >= 2) {
    const headLength = Math.max(10, 4 * lw);
    drawArrow(ctx, viewportPoints[0], viewportPoints[1], headLength);
  } else if (shape.tool === "rect" && shape.points.length >= 2) {
    drawRect(ctx, shape.points as readonly [number, number][], transform);
  } else {
    drawPolyline(ctx, viewportPoints);
  }

  if (selected) {
    drawSelectionOutline(ctx, viewportPoints);
  }

  ctx.restore();
}

/** Draws every shape in order; the shape whose id matches selectedShapeId gets the selection outline. */
export function drawShapes(
  ctx: Canvas2DLike,
  shapes: Shape[],
  transform: ViewportTransform,
  selectedShapeId: string | null,
): void {
  for (const shape of shapes) {
    drawShape(ctx, shape, transform, shape.id === selectedShapeId);
  }
}
